import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Edit, Users, Mail, Phone, DollarSign, Search,
  Calendar, Briefcase, ChevronDown, ChevronUp, Upload, ExternalLink, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = [
  { value: "operations", label: "Operations" },
  { value: "barista", label: "Barista" },
  { value: "management", label: "Management" },
  { value: "kitchen", label: "Kitchen" },
  { value: "marketing", label: "Marketing" },
  { value: "finance", label: "Finance" },
  { value: "hr", label: "Human Resources" },
  { value: "maintenance", label: "Maintenance" },
  { value: "other", label: "Other" },
];

const EMPLOYMENT_TYPES = [
  { value: "full_time", label: "Full-Time" },
  { value: "part_time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "seasonal", label: "Seasonal" },
];

const STAFF_STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "bg-emerald-100 text-emerald-700" },
  { value: "inactive", label: "Inactive", color: "bg-stone-200 text-stone-600" },
  { value: "terminated", label: "Terminated", color: "bg-red-100 text-red-600" },
];

const CANDIDATE_STATUSES = [
  { value: "applied", label: "Applied", color: "bg-stone-100 text-stone-600" },
  { value: "screening", label: "Screening", color: "bg-amber-100 text-amber-700" },
  { value: "interview", label: "Interview", color: "bg-blue-100 text-blue-700" },
  { value: "offer", label: "Offer", color: "bg-violet-100 text-violet-700" },
  { value: "hired", label: "Hired", color: "bg-emerald-100 text-emerald-700" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-600" },
];

const emptyStaffForm = {
  name: "", role: "", department: "operations", email: "", phone: "",
  employment_type: "full_time", status: "active", hire_date: "",
  start_date: "", hourly_rate: "", salary_annual: "", notes: "", store_id: null,
};

const emptyRoleForm = {
  title: "", department: "operations", employment_type: "full_time",
  salary_min: "", salary_max: "", salary_type: "annual",
  description: "", status: "open", store_id: null,
};

const emptyCandidateForm = {
  name: "", email: "", phone: "", linkedin: "", social_urls: {},
  status: "applied", notes: "",
};

export default function StaffManager() {
  const [tab, setTab] = useState("staff"); // "staff" | "roles"
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  // Staff state
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [filterStore, setFilterStore] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Roles state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [expandedRole, setExpandedRole] = useState(null);
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [candidateForm, setCandidateForm] = useState(emptyCandidateForm);
  const [candidateRoleId, setCandidateRoleId] = useState(null);
  const [uploadingCv, setUploadingCv] = useState(false);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchStaff(); }, [filterStore, filterStatus, filterDept]);

  const fetchAll = async () => {
    try {
      const [sRes, stRes, rRes] = await Promise.all([
        axios.get(`${API}/staff`), axios.get(`${API}/stores`), axios.get(`${API}/roles`),
      ]);
      setStaff(sRes.data); setStores(stRes.data); setRoles(rRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStaff = async () => {
    try {
      const params = {};
      if (filterStore !== "all") params.store_id = filterStore;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterDept !== "all") params.department = filterDept;
      setStaff((await axios.get(`${API}/staff`, { params })).data);
    } catch (e) { console.error(e); }
  };

  const fetchRoles = async () => {
    try { setRoles((await axios.get(`${API}/roles`)).data); } catch (e) { console.error(e); }
  };

  // --- Staff CRUD ---
  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    if (!staffForm.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      ...staffForm,
      store_id: staffForm.store_id === "none" ? null : staffForm.store_id,
      hourly_rate: staffForm.hourly_rate ? parseFloat(staffForm.hourly_rate) : null,
      salary_annual: staffForm.salary_annual ? parseFloat(staffForm.salary_annual) : null,
      hire_date: staffForm.hire_date || null,
      start_date: staffForm.start_date || null,
    };
    try {
      if (editingStaff) {
        await axios.put(`${API}/staff/${editingStaff.id}`, payload);
        toast.success("Staff updated");
      } else {
        await axios.post(`${API}/staff`, payload);
        toast.success("Staff added");
      }
      setStaffDialogOpen(false); setStaffForm(emptyStaffForm); setEditingStaff(null);
      fetchStaff();
    } catch (e) { toast.error("Failed to save"); }
  };

  const deleteStaff = async (id) => {
    try { await axios.delete(`${API}/staff/${id}`); toast.success("Staff removed"); fetchStaff(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const openEditStaff = (s) => {
    setEditingStaff(s);
    setStaffForm({
      name: s.name, role: s.role || "", department: s.department || "operations",
      email: s.email || "", phone: s.phone || "",
      employment_type: s.employment_type || "full_time", status: s.status || "active",
      hire_date: s.hire_date || "", start_date: s.start_date || "",
      hourly_rate: s.hourly_rate?.toString() || "",
      salary_annual: s.salary_annual?.toString() || "",
      notes: s.notes || "", store_id: s.store_id || "none",
    });
    setStaffDialogOpen(true);
  };

  // --- Role CRUD ---
  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    if (!roleForm.title.trim()) { toast.error("Title is required"); return; }
    const payload = {
      ...roleForm,
      store_id: roleForm.store_id === "none" ? null : roleForm.store_id,
      salary_min: roleForm.salary_min ? parseFloat(roleForm.salary_min) : null,
      salary_max: roleForm.salary_max ? parseFloat(roleForm.salary_max) : null,
    };
    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, payload);
        toast.success("Role updated");
      } else {
        await axios.post(`${API}/roles`, payload);
        toast.success("Role created");
      }
      setRoleDialogOpen(false); setRoleForm(emptyRoleForm); setEditingRole(null);
      fetchRoles();
    } catch (e) { toast.error("Failed to save"); }
  };

  const deleteRole = async (id) => {
    try { await axios.delete(`${API}/roles/${id}`); toast.success("Role deleted"); fetchRoles(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  // --- Candidate CRUD ---
  const handleCandidateSubmit = async (e) => {
    e.preventDefault();
    if (!candidateForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      await axios.post(`${API}/roles/${candidateRoleId}/candidates`, candidateForm);
      toast.success("Candidate added");
      setCandidateDialogOpen(false); setCandidateForm(emptyCandidateForm);
      fetchRoles();
    } catch (e) { toast.error("Failed to add candidate"); }
  };

  const updateCandidateStatus = async (roleId, candidateId, newStatus) => {
    const role = roles.find(r => r.id === roleId);
    const candidate = role?.candidates?.find(c => c.id === candidateId);
    if (!candidate) return;
    try {
      await axios.put(`${API}/roles/${roleId}/candidates/${candidateId}`, { ...candidate, status: newStatus });
      toast.success("Status updated");
      fetchRoles();
    } catch (e) { toast.error("Failed to update"); }
  };

  const deleteCandidate = async (roleId, candidateId) => {
    try {
      await axios.delete(`${API}/roles/${roleId}/candidates/${candidateId}`);
      toast.success("Candidate removed");
      fetchRoles();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const uploadCv = async (roleId, candidateId, file) => {
    setUploadingCv(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/roles/${roleId}/candidates/${candidateId}/upload-cv`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("CV uploaded");
      fetchRoles();
    } catch (e) { toast.error("Failed to upload CV"); }
    finally { setUploadingCv(false); }
  };

  const getStoreName = (id) => stores.find(s => s.id === id)?.name || "General";
  const isFullTime = (type) => type === "full_time";

  const filteredStaff = staff.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.role || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q);
  });

  const getBadge = (status, options) => {
    const opt = options.find(o => o.value === status);
    return opt ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span> : null;
  };

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="staff-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Team</span>
          </div>
          <h1 data-testid="staff-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Staff & Hiring
          </h1>
          <p className="text-base text-stone-500 mt-2">Manage your team, open roles, and hiring pipeline.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-stone-100 p-1 rounded-lg w-fit">
        <button data-testid="tab-staff" onClick={() => setTab("staff")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === "staff" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}>
          <Users className="w-4 h-4 inline mr-1.5" />Team ({staff.length})
        </button>
        <button data-testid="tab-roles" onClick={() => setTab("roles")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === "roles" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}>
          <Briefcase className="w-4 h-4 inline mr-1.5" />Open Roles ({roles.filter(r => r.status === "open").length})
        </button>
      </div>

      {/* ===== STAFF TAB ===== */}
      {tab === "staff" && (
        <>
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input data-testid="staff-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search staff..." className="pl-9 text-sm" />
            </div>
            <Select value={filterStore} onValueChange={setFilterStore}>
              <SelectTrigger data-testid="filter-staff-store" className="w-40 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stores</SelectItem>
                <SelectItem value="general">General</SelectItem>
                {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger data-testid="filter-staff-dept" className="w-36 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Depts</SelectItem>
                {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={staffDialogOpen} onOpenChange={v => { if (!v) { setStaffDialogOpen(false); setEditingStaff(null); setStaffForm(emptyStaffForm); } else setStaffDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-staff-btn" onClick={() => { setEditingStaff(null); setStaffForm({ ...emptyStaffForm, store_id: filterStore === "all" ? "none" : filterStore }); setStaffDialogOpen(true); }} className="ml-auto bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all font-medium px-5 btn-scale">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Staff
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{editingStaff ? "Edit Staff" : "Add Team Member"}</DialogTitle></DialogHeader>
                <form onSubmit={handleStaffSubmit} className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs font-medium text-stone-600">Name *</Label>
                      <Input data-testid="staff-name" value={staffForm.name} onChange={e => setStaffForm({ ...staffForm, name: e.target.value })} placeholder="Full name" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Role / Title</Label>
                      <Input data-testid="staff-role" value={staffForm.role} onChange={e => setStaffForm({ ...staffForm, role: e.target.value })} placeholder="Head Barista" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Department</Label>
                      <Select value={staffForm.department} onValueChange={v => setStaffForm({ ...staffForm, department: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Store</Label>
                      <Select value={staffForm.store_id || "none"} onValueChange={v => setStaffForm({ ...staffForm, store_id: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">All Stores</SelectItem>
                          {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Employment Type</Label>
                      <Select value={staffForm.employment_type} onValueChange={v => setStaffForm({ ...staffForm, employment_type: v, hourly_rate: isFullTime(v) ? "" : staffForm.hourly_rate, salary_annual: isFullTime(v) ? staffForm.salary_annual : "" })}>
                        <SelectTrigger data-testid="staff-emp-type" className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{EMPLOYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Email</Label>
                      <Input value={staffForm.email} onChange={e => setStaffForm({ ...staffForm, email: e.target.value })} placeholder="email@example.com" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Phone</Label>
                      <Input value={staffForm.phone} onChange={e => setStaffForm({ ...staffForm, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1" />
                    </div>
                    {/* Salary auto-switch based on employment type */}
                    {isFullTime(staffForm.employment_type) ? (
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Salary ($/year)</Label>
                        <Input data-testid="staff-salary-annual" type="number" step="100" value={staffForm.salary_annual} onChange={e => setStaffForm({ ...staffForm, salary_annual: e.target.value })} placeholder="55000" className="mt-1" />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Rate ($/hour)</Label>
                        <Input data-testid="staff-rate" type="number" step="0.25" value={staffForm.hourly_rate} onChange={e => setStaffForm({ ...staffForm, hourly_rate: e.target.value })} placeholder="18.50" className="mt-1" />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Status</Label>
                      <Select value={staffForm.status} onValueChange={v => setStaffForm({ ...staffForm, status: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{STAFF_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Hire Date</Label>
                      <Input type="date" value={staffForm.hire_date} onChange={e => setStaffForm({ ...staffForm, hire_date: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Start Date</Label>
                      <Input type="date" value={staffForm.start_date} onChange={e => setStaffForm({ ...staffForm, start_date: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Notes</Label>
                    <textarea value={staffForm.notes} onChange={e => setStaffForm({ ...staffForm, notes: e.target.value })} rows={2} placeholder="Notes..." className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/5 focus:border-stone-400" />
                  </div>
                  <Button type="submit" data-testid="staff-submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium mt-2">{editingStaff ? "Update" : "Add Team Member"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {filteredStaff.length === 0 && !loading ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{staff.length > 0 ? "No matching staff" : "No team members yet"}</h3>
              <p className="text-sm text-stone-500">{staff.length > 0 ? "Adjust your filters." : "Add team members to get started."}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredStaff.map((s, idx) => (
                <Card key={s.id} data-testid={`staff-card-${s.id}`} className="card-hover bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl animate-slide-up" style={{ animationDelay: `${idx * 0.03}s` }}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-stone-900 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-stone-50">{s.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="font-semibold text-stone-900 text-sm">{s.name}</h3>
                          {getBadge(s.status, STAFF_STATUS_OPTIONS)}
                          <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">{EMPLOYMENT_TYPES.find(t => t.value === s.employment_type)?.label}</span>
                        </div>
                        <p className="text-xs text-stone-500">
                          {s.role && `${s.role} · `}{DEPARTMENTS.find(d => d.value === s.department)?.label}
                          {s.store_id ? ` · ${getStoreName(s.store_id)}` : " · All Stores"}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-stone-500 flex-wrap">
                          {s.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>}
                          {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                          {s.salary_annual != null && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${s.salary_annual.toLocaleString()}/yr</span>}
                          {s.hourly_rate != null && !s.salary_annual && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${s.hourly_rate}/hr</span>}
                          {s.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {s.start_date}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => openEditStaff(s)} className="text-stone-400 hover:text-stone-700 px-2"><Edit className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteStaff(s.id)} className="text-stone-400 hover:text-red-600 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== ROLES TAB ===== */}
      {tab === "roles" && (
        <>
          <div className="flex items-center gap-3 mb-6">
            <Dialog open={roleDialogOpen} onOpenChange={v => { if (!v) { setRoleDialogOpen(false); setEditingRole(null); setRoleForm(emptyRoleForm); } else setRoleDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-role-btn" onClick={() => { setEditingRole(null); setRoleForm(emptyRoleForm); setRoleDialogOpen(true); }} className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all font-medium px-5 btn-scale">
                  <Plus className="w-4 h-4 mr-1.5" /> New Role
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{editingRole ? "Edit Role" : "Create Open Role"}</DialogTitle></DialogHeader>
                <form onSubmit={handleRoleSubmit} className="space-y-3 mt-3">
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Title *</Label>
                    <Input data-testid="role-title" value={roleForm.title} onChange={e => setRoleForm({ ...roleForm, title: e.target.value })} placeholder="Head Barista" className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Department</Label>
                      <Select value={roleForm.department} onValueChange={v => setRoleForm({ ...roleForm, department: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Store</Label>
                      <Select value={roleForm.store_id || "none"} onValueChange={v => setRoleForm({ ...roleForm, store_id: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">All Stores</SelectItem>
                          {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Employment</Label>
                      <Select value={roleForm.employment_type} onValueChange={v => setRoleForm({ ...roleForm, employment_type: v, salary_type: isFullTime(v) ? "annual" : "hourly" })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{EMPLOYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Status</Label>
                      <Select value={roleForm.status} onValueChange={v => setRoleForm({ ...roleForm, status: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="filled">Filled</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Salary Min ($/{isFullTime(roleForm.employment_type) ? "yr" : "hr"})</Label>
                      <Input type="number" value={roleForm.salary_min} onChange={e => setRoleForm({ ...roleForm, salary_min: e.target.value })} placeholder={isFullTime(roleForm.employment_type) ? "45000" : "15"} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Salary Max ($/{isFullTime(roleForm.employment_type) ? "yr" : "hr"})</Label>
                      <Input type="number" value={roleForm.salary_max} onChange={e => setRoleForm({ ...roleForm, salary_max: e.target.value })} placeholder={isFullTime(roleForm.employment_type) ? "60000" : "22"} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Description</Label>
                    <textarea value={roleForm.description} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })} rows={3} placeholder="Role description, requirements..." className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/5 focus:border-stone-400" />
                  </div>
                  <Button type="submit" data-testid="role-submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium">{editingRole ? "Update" : "Create Role"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {roles.length === 0 && !loading ? (
            <div className="text-center py-16">
              <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No open roles yet</h3>
              <p className="text-sm text-stone-500">Create roles you're hiring for and track candidates.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {roles.map((role) => {
                const isExpanded = expandedRole === role.id;
                const candidates = role.candidates || [];
                return (
                  <Card key={role.id} data-testid={`role-card-${role.id}`} className="bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl">
                    <CardContent className="p-0">
                      {/* Role header */}
                      <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedRole(isExpanded ? null : role.id)}>
                        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-4 h-4 text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="font-semibold text-stone-900 text-sm">{role.title}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${role.status === "open" ? "bg-emerald-100 text-emerald-700" : role.status === "filled" ? "bg-blue-100 text-blue-700" : "bg-stone-200 text-stone-600"}`}>{role.status}</span>
                          </div>
                          <p className="text-xs text-stone-500">
                            {DEPARTMENTS.find(d => d.value === role.department)?.label}
                            {role.store_id ? ` · ${getStoreName(role.store_id)}` : " · All Stores"}
                            {role.salary_min && ` · $${role.salary_min.toLocaleString()}${role.salary_max ? `-${role.salary_max.toLocaleString()}` : ""}/${isFullTime(role.employment_type) ? "yr" : "hr"}`}
                            {` · ${candidates.length} candidate${candidates.length !== 1 ? "s" : ""}`}
                          </p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingRole(role); setRoleForm({ title: role.title, department: role.department, employment_type: role.employment_type, salary_min: role.salary_min?.toString() || "", salary_max: role.salary_max?.toString() || "", salary_type: role.salary_type, description: role.description || "", status: role.status, store_id: role.store_id || "none" }); setRoleDialogOpen(true); }} className="text-stone-400 hover:text-stone-700 px-2"><Edit className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteRole(role.id); }} className="text-stone-400 hover:text-red-600 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                        </div>
                      </div>

                      {/* Expanded candidates */}
                      {isExpanded && (
                        <div className="border-t border-stone-100 p-4 bg-stone-50/50">
                          {role.description && <p className="text-xs text-stone-600 mb-3 bg-white p-3 rounded-lg border border-stone-200/60">{role.description}</p>}

                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Candidates ({candidates.length})</p>
                            <Dialog open={candidateDialogOpen && candidateRoleId === role.id} onOpenChange={v => { if (!v) { setCandidateDialogOpen(false); setCandidateForm(emptyCandidateForm); } }}>
                              <DialogTrigger asChild>
                                <Button data-testid={`add-candidate-${role.id}`} size="sm" variant="outline" onClick={() => { setCandidateRoleId(role.id); setCandidateForm(emptyCandidateForm); setCandidateDialogOpen(true); }} className="text-xs h-7 px-3">
                                  <Plus className="w-3 h-3 mr-1" /> Add Candidate
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-md">
                                <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Add Candidate</DialogTitle></DialogHeader>
                                <form onSubmit={handleCandidateSubmit} className="space-y-3 mt-3">
                                  <div>
                                    <Label className="text-xs font-medium text-stone-600">Name *</Label>
                                    <Input value={candidateForm.name} onChange={e => setCandidateForm({ ...candidateForm, name: e.target.value })} placeholder="Full name" className="mt-1" />
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs font-medium text-stone-600">Email</Label>
                                      <Input value={candidateForm.email} onChange={e => setCandidateForm({ ...candidateForm, email: e.target.value })} placeholder="email" className="mt-1" />
                                    </div>
                                    <div>
                                      <Label className="text-xs font-medium text-stone-600">Phone</Label>
                                      <Input value={candidateForm.phone} onChange={e => setCandidateForm({ ...candidateForm, phone: e.target.value })} placeholder="phone" className="mt-1" />
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-xs font-medium text-stone-600">LinkedIn URL</Label>
                                    <Input value={candidateForm.linkedin} onChange={e => setCandidateForm({ ...candidateForm, linkedin: e.target.value })} placeholder="https://linkedin.com/in/..." className="mt-1" />
                                  </div>
                                  <div>
                                    <Label className="text-xs font-medium text-stone-600">Notes</Label>
                                    <textarea value={candidateForm.notes} onChange={e => setCandidateForm({ ...candidateForm, notes: e.target.value })} rows={2} placeholder="Notes about candidate..." className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/5 focus:border-stone-400" />
                                  </div>
                                  <Button type="submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium">Add Candidate</Button>
                                </form>
                              </DialogContent>
                            </Dialog>
                          </div>

                          {candidates.length === 0 ? (
                            <p className="text-xs text-stone-400 text-center py-4">No candidates yet. Add your first one above.</p>
                          ) : (
                            <div className="space-y-2">
                              {candidates.map(c => (
                                <div key={c.id} data-testid={`candidate-${c.id}`} className="bg-white border border-stone-200/60 rounded-lg p-3 flex items-center gap-3">
                                  <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-semibold text-stone-600">{c.name.charAt(0)}</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-stone-900">{c.name}</p>
                                    <div className="flex items-center gap-2 text-xs text-stone-500 flex-wrap">
                                      {c.email && <span>{c.email}</span>}
                                      {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500 flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /> LinkedIn</a>}
                                      {c.cv_doc_id && <span className="text-emerald-600">CV uploaded</span>}
                                    </div>
                                  </div>
                                  <Select value={c.status} onValueChange={v => updateCandidateStatus(role.id, c.id, v)}>
                                    <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CANDIDATE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                  </Select>
                                  <label className="cursor-pointer">
                                    <input type="file" className="hidden" accept=".pdf,.docx,.doc" onChange={e => { if (e.target.files?.[0]) uploadCv(role.id, c.id, e.target.files[0]); e.target.value = ""; }} />
                                    <span className="inline-flex items-center px-2 py-1 text-xs text-stone-500 hover:text-stone-700 hover:bg-stone-50 rounded transition-colors">
                                      {uploadingCv ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 mr-0.5" />} CV
                                    </span>
                                  </label>
                                  <Button variant="ghost" size="sm" onClick={() => deleteCandidate(role.id, c.id)} className="text-stone-400 hover:text-red-600 px-1"><Trash2 className="w-3 h-3" /></Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
